// src/pages/dashboard/components/DashboardSkeleton.tsx
// Animated layout-matched skeleton loader for the Super Admin Dashboard.

export default function DashboardSkeleton() {
  return (
    <div className="dash-scroll scrollbar-none flex-1 overflow-y-auto px-[10px] pb-[28px] pt-0 flex flex-col gap-4 animate-pulse">
      {/* Row 1: KPI Stat Cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="glass rounded-[var(--r-lg)] p-[18px] relative overflow-hidden flex flex-col h-[140px] justify-between">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-[12px] bg-neutral-200 dark:bg-neutral-800/60" />
              <div className="w-12 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
            </div>
            <div className="space-y-2 mt-3.5">
              <div className="w-24 h-8 rounded bg-neutral-200 dark:bg-neutral-800/60" />
              <div className="w-32 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
            </div>
          </div>
        ))}
      </div>

      {/* Row 2: Revenue Chart + Subjects Donut */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-[1.7fr_1fr]">
        {/* Revenue Chart */}
        <div className="glass rounded-[var(--r-lg)] p-5 h-[340px] flex flex-col justify-between">
          <div className="flex justify-between items-center">
            <div className="w-36 h-6 rounded bg-neutral-200 dark:bg-neutral-800/60" />
            <div className="flex gap-2">
              <div className="w-16 h-8 rounded bg-neutral-200 dark:bg-neutral-800/60" />
              <div className="w-16 h-8 rounded bg-neutral-200 dark:bg-neutral-800/60" />
            </div>
          </div>
          <div className="flex items-end gap-3 h-[200px] px-2">
            {[...Array(12)].map((_, idx) => (
              <div
                key={idx}
                className="flex-1 bg-neutral-200 dark:bg-neutral-800/40 rounded-t-md"
                style={{
                  height: `${[40, 60, 45, 75, 55, 90, 80, 65, 85, 50, 70, 95][idx]}%`,
                  opacity: 0.6
                }}
              />
            ))}
          </div>
        </div>

        {/* Subjects Donut */}
        <div className="glass rounded-[var(--r-lg)] p-5 h-[340px] flex flex-col justify-between">
          <div className="w-28 h-6 rounded bg-neutral-200 dark:bg-neutral-800/60" />
          <div className="flex items-center justify-center h-[200px]">
            <div className="w-36 h-36 rounded-full border-[18px] border-neutral-200 dark:border-neutral-800/60 flex items-center justify-center">
              <div className="w-12 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <div className="w-12 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
            <div className="w-12 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
            <div className="w-12 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
          </div>
        </div>
      </div>

      {/* Row 3: Smart Alerts + Enrolments + Plan Distribution */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4">
        {/* Smart Alerts */}
        <div className="glass rounded-[var(--r-lg)] p-5 h-[300px] flex flex-col gap-4">
          <div className="w-32 h-6 rounded bg-neutral-200 dark:bg-neutral-800/60" />
          <div className="space-y-3 flex-1 overflow-hidden">
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="flex gap-3 items-start p-3 rounded-xl bg-neutral-100/50 dark:bg-neutral-800/20">
                <div className="w-8 h-8 rounded-lg bg-neutral-200 dark:bg-neutral-800/60 shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="w-3/4 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
                  <div className="w-1/2 h-3 rounded bg-neutral-200 dark:bg-neutral-800/60" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enrolments */}
        <div className="glass rounded-[var(--r-lg)] p-5 h-[300px] flex flex-col justify-between">
          <div className="w-36 h-6 rounded bg-neutral-200 dark:bg-neutral-800/60" />
          <div className="h-[180px] w-full flex items-end">
            <div className="w-full h-full relative overflow-hidden bg-neutral-100/30 dark:bg-neutral-800/10 rounded-xl flex items-center justify-center">
              <div className="absolute inset-0 flex flex-col justify-between p-2 opacity-30">
                <div className="border-b border-neutral-200 dark:border-neutral-800 w-full" />
                <div className="border-b border-neutral-200 dark:border-neutral-800 w-full" />
                <div className="border-b border-neutral-200 dark:border-neutral-800 w-full" />
              </div>
              <div className="w-5/6 h-24 rounded border-t-2 border-dashed border-neutral-300 dark:border-neutral-700" />
            </div>
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="glass rounded-[var(--r-lg)] p-5 h-[300px] flex flex-col justify-between">
          <div className="w-36 h-6 rounded bg-neutral-200 dark:bg-neutral-800/60" />
          <div className="space-y-4 flex-1 flex flex-col justify-center">
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex justify-between">
                  <div className="w-20 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
                  <div className="w-8 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
                </div>
                <div className="w-full h-3 rounded-full bg-neutral-200/60 dark:bg-neutral-800/40 overflow-hidden">
                  <div className="h-full bg-neutral-300 dark:bg-neutral-700 rounded-full" style={{ width: `${[70, 45, 25][idx]}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 4: Real-time Activity + Top Institutions */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-[1.3fr_1fr]">
        {/* Real-time Activity */}
        <div className="glass rounded-[var(--r-lg)] p-5 h-[320px] flex flex-col gap-4">
          <div className="w-40 h-6 rounded bg-neutral-200 dark:bg-neutral-800/60" />
          <div className="space-y-3 flex-1 overflow-hidden">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b border-neutral-100 dark:border-neutral-800/20">
                <div className="flex gap-3 items-center">
                  <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-800/60" />
                  <div className="space-y-1">
                    <div className="w-32 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
                    <div className="w-20 h-3 rounded bg-neutral-200 dark:bg-neutral-800/60" />
                  </div>
                </div>
                <div className="w-16 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
              </div>
            ))}
          </div>
        </div>

        {/* Top Institutions */}
        <div className="glass rounded-[var(--r-lg)] p-5 h-[320px] flex flex-col gap-4">
          <div className="w-36 h-6 rounded bg-neutral-200 dark:bg-neutral-800/60" />
          <div className="space-y-4 flex-1 overflow-hidden">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-neutral-200 dark:bg-neutral-800/60 flex items-center justify-center text-[10px] font-bold text-neutral-400" />
                  <div className="w-32 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
                </div>
                <div className="w-12 h-4 rounded bg-neutral-200 dark:bg-neutral-800/60" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
